import React, { useState, useEffect } from 'react';
import axios from 'axios';

const LanguageSelectorWithVideoGeneration = ({ 
  userId, 
  currentLanguage, 
  onLanguageChange,
  showVideoGeneration = true 
}) => {
  const [selectedLanguage, setSelectedLanguage] = useState(currentLanguage || 'en');
  const [isGeneratingVideos, setIsGeneratingVideos] = useState(false);
  const [generationStatus, setGenerationStatus] = useState(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [pendingLanguage, setPendingLanguage] = useState(null);

  const languages = [
    { code: 'en', name: 'English', flag: '🇺🇸' },
    { code: 'es', name: 'Spanish', flag: '🇪🇸' },
    { code: 'fr', name: 'French', flag: '🇫🇷' },
    { code: 'de', name: 'German', flag: '🇩🇪' },
    { code: 'it', name: 'Italian', flag: '🇮🇹' },
    { code: 'pt', name: 'Portuguese', flag: '🇵🇹' },
    { code: 'ru', name: 'Russian', flag: '🇷🇺' },
    { code: 'ja', name: 'Japanese', flag: '🇯🇵' },
    { code: 'ko', name: 'Korean', flag: '🇰🇷' },
    { code: 'zh', name: 'Chinese', flag: '🇨🇳' },
    { code: 'ar', name: 'Arabic', flag: '🇸🇦' },
    { code: 'hi', name: 'Hindi', flag: '🇮🇳' },
  ];

  const handleLanguageSelect = (languageCode) => {
    if (languageCode === selectedLanguage) return;

    if (showVideoGeneration && languageCode !== 'en') {
      // Show confirmation dialog for video generation
      setPendingLanguage(languageCode);
      setShowConfirmDialog(true);
    } else {
      // Direct language change for English or when video generation is disabled
      changeLanguage(languageCode, false);
    }
  };

  const changeLanguage = async (languageCode, generateVideos = true) => {
    try {
      setSelectedLanguage(languageCode);
      setShowConfirmDialog(false);
      setPendingLanguage(null);

      // Call the parent component's language change handler
      if (onLanguageChange) {
        onLanguageChange(languageCode);
      }

      // Trigger video generation if enabled and not English
      if (showVideoGeneration && generateVideos && languageCode !== 'en') {
        setIsGeneratingVideos(true);
        setGenerationStatus({ type: 'info', message: 'Initiating video generation...' });

        const response = await axios.post('/api/heygen/language-selected', {
          userId,
          language: languageCode,
          triggerVideoGeneration: true,
        });

        if (response.data.success) {
          const videoGeneration = response.data.videoGeneration;
          
          if (videoGeneration && videoGeneration.success) {
            setGenerationStatus({
              type: 'success',
              message: `Video generation started for ${videoGeneration.processed} locations`,
              details: videoGeneration,
            });
          } else {
            setGenerationStatus({
              type: 'warning',
              message: 'Language changed but no videos were generated',
            });
          }
        } else {
          setGenerationStatus({
            type: 'error',
            message: 'Failed to trigger video generation',
          });
        }
      }
    } catch (error) {
      console.error('Error changing language:', error);
      setGenerationStatus({
        type: 'error',
        message: `Error: ${error.response?.data?.message || error.message}`,
      });
    } finally {
      setIsGeneratingVideos(false);
      
      // Clear status after 10 seconds
      setTimeout(() => {
        setGenerationStatus(null);
      }, 10000);
    }
  };

  const confirmVideoGeneration = () => {
    if (pendingLanguage) {
      changeLanguage(pendingLanguage, true);
    }
  };

  const skipVideoGeneration = () => {
    if (pendingLanguage) {
      changeLanguage(pendingLanguage, false);
    }
  };

  const cancelLanguageChange = () => {
    setShowConfirmDialog(false);
    setPendingLanguage(null);
  };

  useEffect(() => {
    setSelectedLanguage(currentLanguage || 'en');
  }, [currentLanguage]);

  return (
    <div className="language-selector-container">
      <div className="language-selector">
        <label className="language-label">
          🌍 Select Language:
        </label>
        
        <div className="language-dropdown">
          <select
            value={selectedLanguage}
            onChange={(e) => handleLanguageSelect(e.target.value)}
            className="language-select"
            disabled={isGeneratingVideos}
          >
            {languages.map((lang) => (
              <option key={lang.code} value={lang.code}>
                {lang.flag} {lang.name}
              </option>
            ))}
          </select>
        </div>

        {isGeneratingVideos && (
          <div className="generation-spinner">
            <div className="spinner"></div>
            <span>Generating videos...</span>
          </div>
        )}
      </div>

      {/* Generation Status */}
      {generationStatus && (
        <div className={`generation-status ${generationStatus.type}`}>
          <div className="status-message">
            {generationStatus.message}
          </div>
          {generationStatus.details && (
            <div className="status-details">
              <div>✅ Successful: {generationStatus.details.successful}</div>
              <div>❌ Errors: {generationStatus.details.errors}</div>
              <div>📊 Total Processed: {generationStatus.details.processed}</div>
            </div>
          )}
        </div>
      )}

      {/* Confirmation Dialog */}
      {showConfirmDialog && (
        <div className="confirmation-overlay">
          <div className="confirmation-dialog">
            <div className="dialog-header">
              <h3>🎬 Generate Videos for New Language?</h3>
            </div>
            <div className="dialog-content">
              <p>
                You've selected <strong>{languages.find(l => l.code === pendingLanguage)?.name}</strong>.
              </p>
              <p>
                Would you like to automatically generate videos for all your locations in this language?
                This process may take several minutes depending on the number of locations.
              </p>
              <div className="dialog-info">
                <div className="info-item">
                  <span className="info-icon">🔄</span>
                  <span>Text will be automatically translated</span>
                </div>
                <div className="info-item">
                  <span className="info-icon">🎥</span>
                  <span>Videos will be generated using HeyGen AI</span>
                </div>
                <div className="info-item">
                  <span className="info-icon">⏱️</span>
                  <span>Process runs in background</span>
                </div>
              </div>
            </div>
            <div className="dialog-actions">
              <button 
                className="btn btn-primary"
                onClick={confirmVideoGeneration}
                disabled={isGeneratingVideos}
              >
                🚀 Yes, Generate Videos
              </button>
              <button 
                className="btn btn-secondary"
                onClick={skipVideoGeneration}
                disabled={isGeneratingVideos}
              >
                ⏭️ Skip Video Generation
              </button>
              <button 
                className="btn btn-cancel"
                onClick={cancelLanguageChange}
                disabled={isGeneratingVideos}
              >
                ❌ Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      <style jsx>{`
        .language-selector-container {
          margin-bottom: 20px;
        }

        .language-selector {
          display: flex;
          align-items: center;
          gap: 15px;
          padding: 15px;
          background: white;
          border-radius: 8px;
          box-shadow: 0 2px 10px rgba(0,0,0,0.1);
        }

        .language-label {
          font-weight: 600;
          color: #333;
          min-width: 140px;
        }

        .language-dropdown {
          flex: 1;
          max-width: 300px;
        }

        .language-select {
          width: 100%;
          padding: 10px 15px;
          border: 2px solid #dee2e6;
          border-radius: 6px;
          background: white;
          font-size: 16px;
          cursor: pointer;
          transition: border-color 0.3s ease;
        }

        .language-select:focus {
          outline: none;
          border-color: #007bff;
        }

        .language-select:disabled {
          background: #f8f9fa;
          cursor: not-allowed;
        }

        .generation-spinner {
          display: flex;
          align-items: center;
          gap: 10px;
          color: #007bff;
          font-weight: 500;
        }

        .spinner {
          width: 20px;
          height: 20px;
          border: 2px solid #f3f3f3;
          border-top: 2px solid #007bff;
          border-radius: 50%;
          animation: spin 1s linear infinite;
        }

        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        .generation-status {
          margin-top: 15px;
          padding: 15px;
          border-radius: 6px;
          border-left: 4px solid;
        }

        .generation-status.info {
          background: #d1ecf1;
          border-color: #17a2b8;
          color: #0c5460;
        }

        .generation-status.success {
          background: #d4edda;
          border-color: #28a745;
          color: #155724;
        }

        .generation-status.warning {
          background: #fff3cd;
          border-color: #ffc107;
          color: #856404;
        }

        .generation-status.error {
          background: #f8d7da;
          border-color: #dc3545;
          color: #721c24;
        }

        .status-message {
          font-weight: 600;
          margin-bottom: 10px;
        }

        .status-details {
          display: flex;
          gap: 20px;
          font-size: 14px;
          flex-wrap: wrap;
        }

        .confirmation-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0,0,0,0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
        }

        .confirmation-dialog {
          background: white;
          border-radius: 10px;
          box-shadow: 0 10px 30px rgba(0,0,0,0.3);
          max-width: 500px;
          width: 90%;
          max-height: 90vh;
          overflow-y: auto;
        }

        .dialog-header {
          padding: 20px 20px 0 20px;
          border-bottom: 1px solid #dee2e6;
        }

        .dialog-header h3 {
          margin: 0 0 15px 0;
          color: #333;
          font-size: 20px;
        }

        .dialog-content {
          padding: 20px;
        }

        .dialog-content p {
          margin: 0 0 15px 0;
          color: #666;
          line-height: 1.5;
        }

        .dialog-info {
          background: #f8f9fa;
          padding: 15px;
          border-radius: 6px;
          margin: 15px 0;
        }

        .info-item {
          display: flex;
          align-items: center;
          gap: 10px;
          margin-bottom: 8px;
          font-size: 14px;
          color: #666;
        }

        .info-item:last-child {
          margin-bottom: 0;
        }

        .info-icon {
          font-size: 16px;
          min-width: 20px;
        }

        .dialog-actions {
          padding: 0 20px 20px 20px;
          display: flex;
          gap: 10px;
          flex-wrap: wrap;
        }

        .btn {
          padding: 10px 20px;
          border: none;
          border-radius: 6px;
          cursor: pointer;
          font-weight: 500;
          transition: all 0.3s ease;
          flex: 1;
          min-width: 120px;
        }

        .btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .btn-primary {
          background: #28a745;
          color: white;
        }

        .btn-primary:hover:not(:disabled) {
          background: #218838;
        }

        .btn-secondary {
          background: #6c757d;
          color: white;
        }

        .btn-secondary:hover:not(:disabled) {
          background: #545b62;
        }

        .btn-cancel {
          background: #dc3545;
          color: white;
        }

        .btn-cancel:hover:not(:disabled) {
          background: #c82333;
        }

        @media (max-width: 768px) {
          .language-selector {
            flex-direction: column;
            align-items: flex-start;
            gap: 10px;
          }

          .language-label {
            min-width: auto;
          }

          .language-dropdown {
            width: 100%;
            max-width: none;
          }

          .status-details {
            flex-direction: column;
            gap: 5px;
          }

          .dialog-actions {
            flex-direction: column;
          }

          .btn {
            flex: none;
          }
        }
      `}</style>
    </div>
  );
};

export default LanguageSelectorWithVideoGeneration;